import { CommonModule, DOCUMENT } from '@angular/common';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { interval } from 'rxjs';
import { SYSTEM_STATUS_KIND } from '../core/system-status.model';
import { SystemStatusService } from '../core/system-status.service';

@Component({
  selector: 'app-maintenance-page',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './maintenance.page.html',
  styleUrl: './maintenance.page.css',
})
export class MaintenancePageComponent implements OnInit {
  protected readonly systemStatus = inject(SystemStatusService);
  protected readonly outageKind = SYSTEM_STATUS_KIND.Outage;
  private readonly document = inject(DOCUMENT);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    interval(60_000)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.systemStatus
          .refresh()
          .then(() => {
            if (!this.systemStatus.isMaintenance()) {
              this.hardReloadToLogin();
            }
          })
          .catch((error: unknown) => {
            console.warn('Maintenance poll failed:', error);
          });
      });
  }

  async onTryAgain(): Promise<void> {
    await this.systemStatus.refresh();
    if (!this.systemStatus.isMaintenance()) {
      this.hardReloadToLogin();
    }
  }

  hasSafeDetailUrl(): boolean {
    const raw = this.systemStatus.status().detailUrl;
    if (typeof raw !== 'string' || raw.trim().length === 0) {
      return false;
    }
    try {
      const parsed = new URL(raw.trim());
      return parsed.protocol === 'https:' || parsed.protocol === 'http:';
    } catch {
      return false;
    }
  }

  safeDetailUrl(): string {
    const raw = this.systemStatus.status().detailUrl;
    return typeof raw === 'string' ? raw.trim() : '';
  }

  private hardReloadToLogin(): void {
    const view = this.document.defaultView;
    if (view?.location) {
      view.location.assign('/login');
      return;
    }
    this.router.navigateByUrl('/login').catch((error: unknown) => {
      console.warn('Failed to navigate to login after maintenance:', error);
    });
  }
}
